/**
 * The ⓘ words for the new job page (threads/job-page-fresh). Plain words,
 * 7th grade. Pictures (client/public/help/job-page/*.png) are added with the
 * rest of the page.
 */
import type { InfoTopic } from "@/components/info-button";

export const FRESH_HELP: Record<"counts" | "turn" | "when" | "trail" | "notice" | "contact" | "techSees", InfoTopic> = {
    counts: {
        title: "Did it pass?",
        lines: [
            "These numbers count the devices on this job (or units, on jobs that aren't backflow). They never count reports.",
            "The words come from the job type. A backflow test says passed, failed and couldn't do. A repair says repaired, replaced and still open.",
            "Red or amber needs a decision. The device is named right under the numbers.",
            "Couldn't do means the tech wrote down why, like a locked room.",
        ],
        shots: [
            {
                src: "/help/job-page/counts.png",
                alt: "The Did it pass? box",
                marks: [
                    { x: 12, y: 42, label: "Green: done", side: "below" },
                    { x: 26, y: 42, label: "Red: needs a decision", side: "right" },
                    { x: 44, y: 42, label: "The tech wrote why", side: "right" },
                    { x: 30, y: 86, label: "Which ones failed", side: "above" },
                ],
            },
        ],
    },
    turn: {
        title: "Whose turn?",
        lines: [
            "The one person or place this job is waiting on right now. In Closeout it's the same answer as the Closeout card.",
            "Yours means you can do the next step. The button takes you right to it.",
            "Waiting on someone means there's nothing for you to do. It says who.",
        ],
        shots: [
            {
                src: "/help/job-page/turn.png",
                alt: "The Whose turn? box",
                marks: [
                    { x: 35, y: 38, label: "Who the job waits on", side: "below" },
                    { x: 25, y: 82, label: "Does the next step", side: "right" },
                ],
            },
        ],
    },
    when: {
        title: "When & who",
        lines: [
            "The day of the visit and who is going. After the visit, the day they were there.",
            "To move the visit or change the tech, use the classic page for now.",
        ],
        shots: [
            {
                src: "/help/job-page/when.png",
                alt: "The When & who box",
                marks: [
                    { x: 25, y: 38, label: "The visit day", side: "right" },
                    { x: 25, y: 76, label: "Who is going", side: "right" },
                ],
            },
        ],
    },
    trail: {
        title: "The trail",
        lines: [
            "Every job goes left to right: Visit, Completed, City, Customer, Paid.",
            "A green check is done. Amber is yours, and its button does it. Grey is waiting on someone, or not yet.",
            "City shows only when the city takes reports online. A paper city's form goes out with the Customer send.",
        ],
        shots: [
            {
                src: "/help/job-page/trail.png",
                alt: "The trail: Visit, Completed, City, Customer, Paid",
                marks: [
                    { x: 4, y: 35, label: "Green check: done", side: "below" },
                    { x: 36, y: 35, label: "Amber: yours", side: "below" },
                    { x: 69, y: 35, label: "Grey: waiting", side: "below" },
                ],
            },
        ],
    },
    contact: {
        title: "Who to call",
        lines: [
            "The people for this job, most useful first: before the visit, the site contact (who lets us in); after it, the person who pays.",
            "Call, Text and Email reach that person in one tap. Every call, text and email is saved on the job, and the top line says who reached out last and how it went.",
            "No Text button means they haven't said yes to texts. A red email bounced. Use Fix email or Add phone to correct it.",
        ],
        shots: [
            {
                src: "/help/job-page/contact.png",
                alt: "The Who to call block",
                marks: [
                    { x: 20, y: 11, label: "Who reached out last", side: "below" },
                    { x: 27, y: 53, label: "Bounced: fix it here", side: "below" },
                    { x: 5, y: 80, label: "One tap, saved on the job", side: "above" },
                    { x: 60, y: 80, label: "Text only with a yes", side: "above" },
                ],
            },
        ],
    },
    techSees: {
        title: "This is what the tech sees",
        lines: [
            "This is the same box the tech reads first on his iPad, word for word.",
            "One line per device: what to do to it and what's on the truck. The bottom is for the whole job: gate, call-ahead, IVR.",
            "Edit changes it for him too. If he's already booked, his iPad marks the line that changed until he taps Got it.",
        ],
        shots: [
            {
                src: "/help/job-page/tech-sees.png",
                alt: "This is what the tech sees",
                marks: [
                    { x: 30, y: 6, label: "The same box as his iPad", side: "below" },
                    { x: 91, y: 6, label: "Change it", side: "below" },
                    { x: 35, y: 42, label: "One line per device", side: "right" },
                ],
            },
        ],
    },
    notice: {
        title: "The new job page",
        lines: [
            "The new job page was rebuilt from scratch. The top answers three things: did it pass, whose turn it is, and when.",
            "Only your screen changes. Switch back to the classic page any time, top right.",
        ],
    },
};
